import { apiClient } from './api-client';

/**
 * Onboarding step identifiers in sequential order.
 * A participant must complete each step before proceeding to the next.
 */
export type OnboardingStep =
  | 'profile'
  | 'idv'
  | 'consent'
  | 'ready';

/**
 * Result of the onboarding status check.
 */
export interface OnboardingStatus {
  /** The next step the participant needs to complete */
  currentStep: OnboardingStep;
  /** Whether all onboarding steps are complete (ready to record) */
  isComplete: boolean;
  /** Route to redirect the participant to for their current step */
  redirectPath: string;
}

/** Maps each incomplete step to the route the user should be redirected to */
const STEP_ROUTES: Record<OnboardingStep, string> = {
  profile: '/profile',
  idv: '/identity-verification',
  consent: '/consent',
  ready: '/record',
};

/**
 * Checks the participant's onboarding status by querying profile, IDV, and consent
 * endpoints sequentially. Returns which step needs to be completed next.
 *
 * Gate order:
 * 1. Profile complete (gender, age_range, accent_label, preferred_environment)
 * 2. IDV verified
 * 3. Consent accepted (current version)
 *
 * If all gates pass, returns { currentStep: 'ready', isComplete: true }.
 */
export async function getOnboardingStatus(): Promise<OnboardingStatus> {
  // Step 1: Check profile completion
  try {
    const profileRes = await apiClient.get('/participants/me');
    const profile = profileRes.data?.profile;
    const isProfileComplete =
      profile?.gender &&
      profile?.age_range &&
      profile?.accent_label &&
      profile?.preferred_environment;

    if (!isProfileComplete) {
      return {
        currentStep: 'profile',
        isComplete: false,
        redirectPath: STEP_ROUTES.profile,
      };
    }
  } catch {
    // If profile fetch fails, assume incomplete
    return {
      currentStep: 'profile',
      isComplete: false,
      redirectPath: STEP_ROUTES.profile,
    };
  }

  // Step 2: IDV check — auto-verified for all users for now
  // When IDV is re-enabled, uncomment the block below.
  // try {
  //   const idvRes = await apiClient.get('/participants/me/idv/status');
  //   const idvStatus = idvRes.data?.status;
  //
  //   if (idvStatus !== 'verified') {
  //     return {
  //       currentStep: 'idv',
  //       isComplete: false,
  //       redirectPath: STEP_ROUTES.idv,
  //     };
  //   }
  // } catch {
  //   return {
  //     currentStep: 'idv',
  //     isComplete: false,
  //     redirectPath: STEP_ROUTES.idv,
  //   };
  // }

  // Step 3: Consent check
  try {
    const consentRes = await apiClient.get('/consent/status');
    const consentData = consentRes.data;

    if (!consentData?.accepted || consentData?.versionMismatch) {
      return {
        currentStep: 'consent',
        isComplete: false,
        redirectPath: STEP_ROUTES.consent,
      };
    }
  } catch {
    return {
      currentStep: 'consent',
      isComplete: false,
      redirectPath: STEP_ROUTES.consent,
    };
  }

  // All gates passed
  return {
    currentStep: 'ready',
    isComplete: true,
    redirectPath: STEP_ROUTES.ready,
  };
}
